import React, { useEffect } from 'react'

import { Box, Button, Text } from '@factorialco/deprecated-design-system'
import { Input } from '@factorialco/f0-react/dist/experimental'
import {
  EmployeeForSelectFragment,
  SpaceControlBookingPoliciesDocument,
} from 'generated/resources/graphql'

import { observer } from 'mobx-react'

import Sidepanel from 'modules/core/components/Sidepanel'
import { useFormContext } from 'modules/core/hooks/useForm'
import { useQuery } from 'modules/core/hooks/useQuery'
import { track, trackingEvents } from 'modules/core/lib/Tracking'
import i18n from 'modules/core/lib/i18n'

import { EntitySelectorWrapper } from 'modules/employees/components/EmployeeSelector/EntitySelectorWrapper'
import BookingSlots from 'modules/space_control/components/BookingsSidepanel/BookingSlots/index'
import SidepanelInfo from 'modules/space_control/components/BookingsSidepanel/SidepanelInfo/index'
import type { WorkSpace as WorkSpaceNode } from 'modules/space_control/hooks/useWorkSpace'
import type { WorkSpaceSlotNode } from 'modules/space_control/hooks/useWorkSpaceSlots'
import type { SlotSetup } from 'modules/space_control/lib/enums'

interface bookingFormFields {
  accessId: number
  date: string
  slots?: number[]
}

interface Props {
  onClose: () => void
  workSpace: WorkSpaceNode
  slots: WorkSpaceSlotNode[]
  isSlotsLoading: boolean
  edit?: boolean
  bookedSlots?: WorkSpaceSlotNode[]
  onChangeDate?: (date: string) => void
  selectedEmployee?: EmployeeForSelectFragment | null
  onSelectedEmployeeChange?: (employee: EmployeeForSelectFragment | null) => void
}

const BookingSidepanel = ({
  onClose,
  workSpace,
  slots,
  isSlotsLoading,
  edit,
  bookedSlots = [],
  selectedEmployee,
  onSelectedEmployeeChange,
  onChangeDate,
}: Props) => {
  const bookingForm = useFormContext<bookingFormFields>()
  const isHalfDay = workSpace?.slotSetup === 'half_day'

  const { data: policiesData, loading: policiesLoading } = useQuery(
    SpaceControlBookingPoliciesDocument,
    {
      fetchPolicy: 'network-only',
    }
  )
  const policies = policiesData?.spaceControl._policies
  const timezone = workSpace.workArea?.location?.timezone

  React.useEffect(() => {
    bookingForm.setFieldValue('accessId', selectedEmployee?.access?.id ?? -1)
  }, [selectedEmployee])

  const handleEmployeeChange = (employee: EmployeeForSelectFragment | null) => {
    onSelectedEmployeeChange?.(employee)
  }

  const handleSubmit = () => {
    bookingForm.handleSubmit()
    onClose()
  }

  const handleCancel = () => {
    track(trackingEvents.spaceControl.bookings.cancelBooking)
    onClose()
  }

  const onChangeTimeSlots = (value: number) => {
    const currentValues = bookingForm.values.slots
    if (currentValues && currentValues?.length > 0) {
      if (currentValues?.includes(value)) {
        const filteredValues = currentValues.filter((slot) => slot !== value)
        bookingForm.setFieldValue('slots', filteredValues)
      } else {
        bookingForm.setFieldValue('slots', [...currentValues, value])
      }
    } else {
      bookingForm.setFieldValue('slots', [value])
    }
  }

  useEffect(() => {
    const selectedUnavailableSlots = bookingForm.values.slots?.filter((value) =>
      bookedSlots.some((slot) => slot.slotNumber === value)
    )
    if (
      selectedUnavailableSlots &&
      selectedUnavailableSlots.length > 0 &&
      bookingForm.values.slots
    ) {
      bookingForm.setFieldValue(
        'slots',
        bookingForm.values.slots.filter((value) => !selectedUnavailableSlots.includes(value))
      )
    }
  }, [bookedSlots])

  if (workSpace)
    return (
      <Sidepanel
        onClose={onClose}
        closeLabel="close"
        title={
          edit
            ? i18n.t('space_control.overview.booking_sidepanel.edit_title')
            : i18n.t('space_control.overview.booking_sidepanel.title')
        }
        open
        footer={
          <>
            <Button onClick={handleCancel} type="secondary">
              {i18n.t('space_control.overview.booking_sidepanel.cancel')}
            </Button>
            <Button
              onClick={handleSubmit}
              disabled={
                bookingForm.values.accessId === -1 ||
                (workSpace.slotSetup !== 'whole_day' &&
                  bookingForm.values.slots?.length === 0) ||
                (workSpace.slotSetup === 'whole_day' && slots?.[0]?.currentCapacity === 0)
              }
            >
              {edit
                ? i18n.t('space_control.overview.booking_sidepanel.save_changes')
                : i18n.t('space_control.overview.booking_sidepanel.confirm')}
            </Button>
          </>
        }
      >
        <SidepanelInfo workSpace={workSpace} />
        <Box paddingTop="s32" gap="s24">
          <EntitySelectorWrapper
            disabled={policiesLoading || !policies?.bookingEditEverybody?.can}
            usingAccessIds
            label={i18n.t('common.employees')}
            onSelect={handleEmployeeChange}
            value={selectedEmployee ?? null}
          />

          <Box gap="s8">
            <Input
              type="date"
              label={i18n.t('space_control.overview.booking_sidepanel.date_field_label')}
              value={bookingForm.values.date}
              onChange={(value: string) => {
                if (onChangeDate) {
                  onChangeDate(value)
                }
                bookingForm.setFieldValue('date', value)
              }}
            />
            {bookingForm.errors.date && (
              <Text.Normal size="100" color="moodSuperNegative">
                {bookingForm.errors.date}
              </Text.Normal>
            )}
          </Box>
          {!isSlotsLoading && workSpace.slotSetup !== 'whole_day' && (
            <BookingSlots
              slots={slots}
              onChange={onChangeTimeSlots}
              selectedValues={bookingForm.values.slots ?? []}
              isHalfDay={isHalfDay}
              timezone={timezone}
              selectedDate={bookingForm.values.date}
            ></BookingSlots>
          )}
        </Box>
      </Sidepanel>
    )
  return <></>
}

export default observer(BookingSidepanel)
